import { useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { AuthRouteTables, Localization } from "../types/types";
import { LocalizationContext } from "../context/context";
import { MatchesProvider } from "../context/MatchProvider";
import { LivestreamChatProvider } from "../context/LivestreamChatProvider";
import { theme } from "../styles/theme";

import { Home } from "./home/Home";
import { Teams } from "./teams/Teams";
import { Header } from "./shared/Header";
import { Footer } from "./shared/Footer";
import { AdSideRailLayout } from "./shared/AdSideRailLayout";
import { About } from "./about/About";
import { ScrollToTop } from "./shared/ScrollToTop";
import { SkipLink } from "./shared/SkipLink";
import { Gallery } from "./gallery/Gallery";
import { LeaderboardPage } from "./leaderboard/LeaderboardPage";
import { SeasonStatsPage } from "./seasonstats/SeasonStatsPage";
import { ChampionStatsPage } from "./championstats/ChampionStatsPage";
import { TeamsProvider } from "../context/TeamsProvider";
import { PlayerPage } from "./teams/players/PlayerPage";
import { SchedulePage } from "./schedule/SchedulePage";
import { ResultsPage } from "./results/Results";
import { MatchDetailPage } from "./match/MatchDetailPage";
import { CookieConsentBanner } from "./shared/CookieConsentBanner";
import { NotFoundPage } from "./shared/NotFoundPage";
import { readAdsConsent } from "../ads/consent";
import { trackLocalPageView } from "../analytics/localAnalytics";
import { initSessionNavigation, trackSessionNavigation } from "../analytics/sessionAnalytics";

import { CookieConsentProvider } from "../context/CookieConsentProvider";
import { TermsPage } from "./legal/TermsPage";
import { PrivacyPage } from "./legal/PrivacyPage";
import { Login } from "./admin/Login";
import { ProtectedRoute } from "./admin/ProtectedRoute";
import { renderAdminRoutes } from "./admin/AdminRoutes";
import { LocaleOutlet } from "./routing/LocaleOutlet";
import { LegacyLocaleRedirect, RootLocaleRedirect } from "./routing/LocaleRedirects";
import { TcgCollectionPage } from "./tcg/TcgCollectionPage";
import {
  inferLanguage,
  isLocalePrefix,
  preferredLocalePrefix,
  prefixToLocalization,
  splitLocalePath,
  withLocalePrefix,
} from "../utils/localePaths";

const LANGUAGE_KEY = "finnhouse_language_v1";

const readStoredLanguage = (): Localization | null => {
  try {
    const consent = typeof window !== "undefined" ? readAdsConsent() : "unset";
    if (consent !== "accepted") return null;
    const stored = window.localStorage.getItem(LANGUAGE_KEY);
    if (stored === Localization.Finnish || stored === Localization.English) return stored;
  } catch {
    // ignore
  }
  return null;
};

export const App = () => {
  const location = useLocation();
  const hideLayout = /admin|\/tcg\//i.test(location.pathname);
  const { prefix: urlPrefix } = splitLocalePath(location.pathname);

  const initialLanguage = useMemo<Localization>(() => {
    if (urlPrefix && isLocalePrefix(urlPrefix)) {
      return prefixToLocalization(urlPrefix);
    }
    return readStoredLanguage() ?? inferLanguage();
  }, [urlPrefix]);

  const [language, setLanguage] = useState<Localization>(initialLanguage);

  useEffect(() => {
    if (urlPrefix && isLocalePrefix(urlPrefix)) {
      setLanguage(prefixToLocalization(urlPrefix));
    }
  }, [urlPrefix]);

  useEffect(() => {
    try {
      const consent = readAdsConsent();
      if (consent !== "accepted") return;
      window.localStorage.setItem(LANGUAGE_KEY, language);
    } catch {
      // ignore
    }
  }, [language]);

  useEffect(() => {
    initSessionNavigation();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    trackLocalPageView(path);
    trackSessionNavigation(path);
  }, [location.pathname, location.search]);

  const defaultPrefix = preferredLocalePrefix();

  return (
    <ThemeProvider theme={theme}>
      <MatchesProvider>
        <TeamsProvider>
          <LocalizationContext.Provider value={language}>
            <CookieConsentProvider>
              <LivestreamChatProvider>
                {!hideLayout && (
                  <>
                    <SkipLink />
                    <Header language={language} setLanguage={setLanguage} />
                    <ScrollToTop />
                    {/* Fixed overlay — keep out of <main> flex/gap so it cannot affect CLS */}
                    <CookieConsentBanner />
                  </>
                )}

                <main id="main-content" tabIndex={-1}>
                  <AdSideRailLayout>
                    <Routes>
                      <Route path={AuthRouteTables.AUTH} element={<Login />} />
                      <Route path={AuthRouteTables.ADMIN} element={<ProtectedRoute />}>
                        {renderAdminRoutes()}
                      </Route>

                      <Route path="/:locale" element={<LocaleOutlet />}>
                        <Route index element={<Home />} />
                        <Route
                          path="fpl/scoreboards"
                          element={
                            <Navigate to={withLocalePrefix("/results", defaultPrefix)} replace />
                          }
                        />
                        <Route path="fpl/leaderboard" element={<LeaderboardPage />} />
                        <Route path="fpl/stats" element={<SeasonStatsPage />} />
                        <Route path="fpl/champions" element={<ChampionStatsPage />} />
                        <Route path="fpl/players/:playerName" element={<PlayerPage />} />
                        <Route path="fpl/teams/*" element={<Teams />} />
                        <Route path="gallery" element={<Gallery />} />
                        <Route path="about" element={<About />} />
                        <Route path="terms" element={<TermsPage />} />
                        <Route path="privacy" element={<PrivacyPage />} />
                        <Route path="schedule" element={<SchedulePage />} />
                        <Route path="results" element={<ResultsPage />} />
                        <Route path="matches/:matchId" element={<MatchDetailPage />} />
                        <Route path="tcg/:slug" element={<TcgCollectionPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>

                      <Route path="/" element={<RootLocaleRedirect />} />
                      <Route path="/about" element={<LegacyLocaleRedirect />} />
                      <Route path="/schedule" element={<LegacyLocaleRedirect />} />
                      <Route path="/results" element={<LegacyLocaleRedirect />} />
                      <Route path="/gallery" element={<LegacyLocaleRedirect />} />
                      <Route path="/terms" element={<LegacyLocaleRedirect />} />
                      <Route path="/privacy" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/leaderboard" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/stats" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/champions" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/teams/*" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/players/:playerName" element={<LegacyLocaleRedirect />} />
                      <Route path="/fpl/scoreboards" element={<LegacyLocaleRedirect />} />
                      <Route path="/matches/:matchId" element={<LegacyLocaleRedirect />} />
                      <Route path="/tcg/:slug" element={<LegacyLocaleRedirect />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AdSideRailLayout>
                </main>

                {/* Outside <main>: avoids mt-auto/gap coupling that attributes large CLS to the footer */}
                {!hideLayout && <Footer />}
              </LivestreamChatProvider>
            </CookieConsentProvider>
          </LocalizationContext.Provider>
        </TeamsProvider>
      </MatchesProvider>
    </ThemeProvider>
  );
};
